import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Box, Paper, InputBase, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const MapContainerStyled = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 0;
  
  .leaflet-container {
    height: 100%;
    width: 100%;
  }
  
  .blue-marker {
    background-color: #4285F4;
    border-radius: 50%;
    border: 2px solid white;
    width: 16px;
    height: 16px;
    box-shadow: 0 0 0 rgba(66, 133, 244, 0.4);
    animation: pulse 2s infinite;
  }
`;

const SearchBar = styled(Paper)`
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  z-index: 1000;
  padding: 2px 4px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border-radius: 24px !important;
`;

const PressHoldOverlay = styled.div`
  position: absolute;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(255, 255, 255, 0.8);
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: bold;
  color: #ff0000;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

// Component to handle map center changes
const ChangeMapView = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, 15, { duration: 2 });
    }
  }, [center, map]);
  return null;
};

// Custom blue dot marker icon
const createBlueMarkerIcon = () => {
  return L.divIcon({
    className: 'blue-marker-icon',
    html: '<div class="blue-marker"></div>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
};

const MapView = ({ onPressAndHold }) => {
  const [position, setPosition] = useState([37.7749, -122.4194]); // Default to San Francisco
  const [pressTimer, setPressTimer] = useState(null);
  const [isPressing, setIsPressing] = useState(false);
  const mapRef = useRef(null);

  // Get user's location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setPosition([latitude, longitude]);
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  }, []);

  // Handle press and hold events
  const handleTouchStart = (e) => {
    setIsPressing(true);
    const timer = setTimeout(() => {
      onPressAndHold();
      setIsPressing(false);
    }, 1000); // 1 second press and hold
    setPressTimer(timer);
  };

  const handleTouchEnd = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }
    setIsPressing(false);
  };

  const handleMouseDown = (e) => {
    setIsPressing(true);
    const timer = setTimeout(() => {
      onPressAndHold();
      setIsPressing(false);
    }, 1000); // 1 second press and hold
    setPressTimer(timer);
  };

  const handleMouseUp = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }
    setIsPressing(false);
  };

  return (
    <>
      <SearchBar elevation={1}>
        <IconButton sx={{ p: '10px' }} aria-label="search">
          <SearchIcon />
        </IconButton>
        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder="Find Musicians Near You..."
          inputProps={{ 'aria-label': 'find musicians' }}
        />
        <IconButton sx={{ p: '10px' }} aria-label="menu">
          <MenuIcon />
        </IconButton>
      </SearchBar>

      <MapContainerStyled
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <MapContainer 
          center={position} 
          zoom={15} 
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={position} icon={createBlueMarkerIcon()} />
          <Circle 
            center={position} 
            radius={300} 
            pathOptions={{ 
              color: '#4285F4', 
              fillColor: '#4285F4', 
              fillOpacity: 0.2,
              weight: 2,
              opacity: 0.5
            }} 
          />
          <ChangeMapView center={position} />
        </MapContainer>
      </MapContainerStyled>

      {isPressing && (
        <PressHoldOverlay>PRESS AND HOLD</PressHoldOverlay>
      )}
    </>
  );
};

export default MapView;
